import { useState } from 'react'
import { AxiosError } from 'axios'
import { useNavigate } from 'react-router-dom'

import { useImportSurgeries } from 'lib/services/api/surgeries/import-surgeries'
import { convertToCSVIfNeeded } from 'lib/utils/excelToCsv'

import {
  ImportScheduleProps,
  ImportSummaryData,
  UseImportScheduleLogicReturn,
} from './types'

const useImportScheduleLogic = (
  props: ImportScheduleProps
): UseImportScheduleLogicReturn => {
  const { onUploadSuccess, onClose } = props
  const navigate = useNavigate()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false)
  const [importSummary, setImportSummary] = useState<ImportSummaryData | null>(
    null
  )
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'success' | 'error' | 'info' | 'warning',
  })

  const { mutateAsync: importFile } = useImportSurgeries()

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      try {
        setIsUploading(true)
        // Convert Excel to CSV if needed
        const processedFile = await convertToCSVIfNeeded(file)
        setSelectedFile(processedFile)
        setIsConfirmDialogOpen(true)
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to process Excel file. Please try again.',
          severity: 'error',
        })
      } finally {
        setIsUploading(false)
      }
    } else {
      setSelectedFile(null)
    }
  }

  const handleConfirmUpload = async () => {
    if (!selectedFile) return

    setIsUploading(true)
    setUploadError(null)

    try {
      const data = await importFile({ file: selectedFile, anonymous: false })
      setImportSummary(data as ImportSummaryData)
      setIsSummaryDialogOpen(true)
      setSnackbar({
        open: true,
        message: `Successfully imported ${data.count} surgeries.`,
        severity: 'success',
      })
      if (onUploadSuccess) onUploadSuccess(data)
    } catch (error) {
      const errorMessage =
        error instanceof AxiosError
          ? error.response?.data?.detail ||
            error.message ||
            'An error occurred during upload.'
          : 'An error occurred during upload.'
      setUploadError(errorMessage)
      setSnackbar({
        open: true,
        message:
          'Failed to upload file. Please make sure that the file is a valid Excel file and that it matches the expected format.',
        severity: 'error',
      })
    } finally {
      setIsUploading(false)
    }
  }

  const handleCloseConfirmDialog = () => {
    setIsConfirmDialogOpen(false)
  }

  const handleCloseSummaryDialog = () => {
    setIsSummaryDialogOpen(false)
    if (onClose) onClose()

    // Navigate back to surgeries list after closing the import summary
    navigate('/surgeries')
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  return {
    selectedFile,
    isConfirmDialogOpen,
    isSummaryDialogOpen,
    importSummary,
    isUploading,
    uploadError,
    snackbar,
    handleFileChange,
    handleConfirmUpload,
    handleCloseConfirmDialog,
    handleCloseSummaryDialog,
    handleCloseSnackbar,
  }
}

export default useImportScheduleLogic
